/**
 * Events Page
 * Displays list of all events with filters
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { geocodeCityState } from '../utils/geocoding';

const GENRE_LIST = [
  'Ambient', 'Alternative', 'Blues', 'Classical', 'Country', 'Cultural',
  'Dance', 'EDM', 'Electronic', 'Emo', 'Experimental', 'Folk', 'Grunge',
  'Hardcore', 'Hip-hop', 'Instrumental', 'Jazz', 'Metal', 'Noise', 'Pop',
  'Psychedelic', 'Punk', 'R&B', 'Rap', 'Reggae-Ska', 'Religious', 'Rock',
  'Shoegaze', 'Singer-Songwriter', 'Spoken Word'
];

const EventsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedAges, setSelectedAges] = useState([]);
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [filterRadius, setFilterRadius] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [locationError, setLocationError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);

  // Resolved lat/lon from the location inputs (set when user applies filters)
  const [filterLat, setFilterLat] = useState(null);
  const [filterLon, setFilterLon] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const buildQueryParams = (lat, lon) => {
    const params = new URLSearchParams();

    if (dateRange) params.append('dateRange', dateRange);
    if (selectedGenres.length > 0) params.append('genres', selectedGenres.join(','));
    if (selectedAges.length > 0) params.append('ageRestriction', selectedAges.join(','));
    if (sortBy) params.append('sortBy', sortBy);

    // Only send location params if we have all three
    if (lat && lon && filterRadius) {
      params.append('filterLat', lat);
      params.append('filterLon', lon);
      params.append('filterRadius', filterRadius);
    }

    return params.toString();
  };

  const fetchEvents = async (lat = filterLat, lon = filterLon) => {
    setLoading(true);
    setError('');
    try {
      const queryString = buildQueryParams(lat, lon);
      const url = queryString ? `/events?${queryString}` : '/events';
      const response = await apiClient.get(url);
      if (response.data.success) {
        setEvents(response.data.events);
      }
    } catch (err) {
      setError('Failed to load events');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenreToggle = (genre) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  const handleAgeToggle = (age) => {
    setSelectedAges(prev =>
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const handleApplyFilters = async () => {
    setLocationError('');

    // If location fields are filled, geocode them first
    if (locationCity && locationState) {
      if (!filterRadius) {
        setLocationError('Please enter a distance radius when filtering by location.');
        return;
      }

      setLocationLoading(true);
      try {
        const result = await geocodeCityState(locationCity, locationState);
        setFilterLat(result.latitude);
        setFilterLon(result.longitude);
        await fetchEvents(result.latitude, result.longitude);
      } catch (err) {
        setLocationError(err.message);
        setFilterLat(null);
        setFilterLon(null);
      } finally {
        setLocationLoading(false);
      }
    } else {
      // No location filter — clear any previously resolved coords
      setFilterLat(null);
      setFilterLon(null);
      await fetchEvents(null, null);
    }
  };

  const handleClearFilters = () => {
    setDateRange('');
    setSelectedGenres([]);
    setSelectedAges([]);
    setLocationCity('');
    setLocationState('');
    setFilterRadius('');
    setSortBy('date');
    setFilterLat(null);
    setFilterLon(null);
    setLocationError('');
    // Fetch with no filters
    const params = new URLSearchParams();
    setLoading(true);
    apiClient.get('/events').then(response => {
      if (response.data.success) setEvents(response.data.events);
    }).catch(() => {
      setError('Failed to load events');
    }).finally(() => setLoading(false));
  };

  const activeFilterCount = () => {
    let count = 0;
    if (dateRange) count++;
    if (selectedGenres.length > 0) count++;
    if (selectedAges.length > 0) count++;
    if (locationCity && locationState && filterRadius) count++;
    if (sortBy !== 'date') count++;
    return count;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '50px auto', padding: '20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>All Events</h1>
        <button
          onClick={() => navigate('/')}
          style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Back to Home
        </button>
      </div>

      {/* Filter Toggle Button */}
      <div style={{ marginBottom: '15px' }}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            padding: '10px 20px',
            backgroundColor: activeFilterCount() > 0 ? '#007bff' : '#e9ecef',
            color: activeFilterCount() > 0 ? 'white' : '#333',
            border: '1px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: activeFilterCount() > 0 ? 'bold' : 'normal'
          }}
        >
          {showFilters ? 'Hide Filters' : `Filters${activeFilterCount() > 0 ? ` (${activeFilterCount()} active)` : ''}`}
        </button>

        {activeFilterCount() > 0 && (
          <button
            onClick={handleClearFilters}
            style={{ marginLeft: '10px', padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Clear All
          </button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '25px' }}>

          {/* Row 1: Date Range + Sort */}
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Date Range</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '160px' }}
              >
                <option value="">All upcoming</option>
                <option value="7days">Next 7 days</option>
                <option value="14days">Next 2 weeks</option>
                <option value="30days">Next 30 days</option>
              </select>
            </div>

            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '160px' }}
              >
                <option value="date">Soonest First</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>

          {/* Row 2: Location + Radius */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Location Filter</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <input
                type="text"
                placeholder="City"
                value={locationCity}
                onChange={(e) => setLocationCity(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '150px' }}
              />
              <input
                type="text"
                placeholder="ST"
                value={locationState}
                onChange={(e) => setLocationState(e.target.value.toUpperCase())}
                maxLength="2"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '60px' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="number"
                  placeholder="Miles"
                  value={filterRadius}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!e.target.value) setFilterRadius('');
                    else if (val >= 1 && val <= 999) setFilterRadius(String(val));
                  }}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', width: '80px' }}
                />
                <span style={{ color: '#666', fontSize: '14px' }}>mi radius</span>
              </div>
            </div>
            {locationError && (
              <p style={{ color: '#dc3545', fontSize: '14px', marginTop: '6px' }}>{locationError}</p>
            )}
            <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
              All three fields required for location filtering
            </p>
          </div>

          {/* Row 3: Age Restriction */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Age Restriction</label>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {[
                { value: 'all_ages', label: 'All Ages' },
                { value: '18_plus', label: '18+' },
                { value: '21_plus', label: '21+' }
              ].map(({ value, label }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedAges.includes(value)}
                    onChange={() => handleAgeToggle(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Row 4: Genre Tags */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Genre Tags {selectedGenres.length > 0 && <span style={{ color: '#007bff' }}>({selectedGenres.length} selected)</span>}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '6px' }}>
              {GENRE_LIST.map(genre => (
                <label key={genre} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedGenres.includes(genre)}
                    onChange={() => handleGenreToggle(genre)}
                  />
                  {genre}
                </label>
              ))}
            </div>
          </div>

          {/* Apply Button */}
          <button
            onClick={handleApplyFilters}
            disabled={locationLoading}
            style={{
              padding: '10px 30px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: locationLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {locationLoading ? 'Locating...' : 'Apply Filters'}
          </button>
        </div>
      )}

      {/* Results Count */}
      {!loading && (
        <p style={{ color: '#666', marginBottom: '15px' }}>
          {events.length === 0 ? 'No events found' : `${events.length} event${events.length !== 1 ? 's' : ''} found`}
        </p>
      )}

      {/* Events List */}
      {loading ? (
        <div style={{ padding: '50px', textAlign: 'center' }}>Loading events...</div>
      ) : error ? (
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <p style={{ color: 'red' }}>{error}</p>
          <button onClick={() => fetchEvents()}>Try Again</button>
        </div>
      ) : events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>No events match your filters.</p>
          {activeFilterCount() > 0 && (
            <button
              onClick={handleClearFilters}
              style={{ marginTop: '15px', padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {events.map((event) => (
            <div
              key={event.id}
              onClick={() => navigate(`/events/${event.id}`)}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '20px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                transition: 'box-shadow 0.2s',
                display: 'flex',
                gap: '20px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              {/* Flyer Image */}
              <div style={{ flexShrink: 0 }}>
                <img
                  src={event.flyer_url}
                  alt={event.title}
                  style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/150x150?text=No+Image'; }}
                />
              </div>

              {/* Event Info */}
              <div style={{ flex: 1 }}>
                <h2 style={{ marginTop: 0, marginBottom: '10px' }}>{event.title}</h2>
                <p style={{ margin: '5px 0', color: '#666' }}><strong>📅 Date:</strong> {formatDate(event.event_date)}</p>
                <p style={{ margin: '5px 0', color: '#666' }}><strong>🕐 Doors:</strong> {formatTime(event.door_time)} | <strong>Music:</strong> {formatTime(event.music_time)}</p>
                <p style={{ margin: '5px 0', color: '#666' }}><strong>📍 Location:</strong> {event.city}, {event.state}</p>
                <p style={{ margin: '5px 0', color: '#666' }}><strong>💵 Price:</strong> {event.ticket_price === 0 ? 'Free' : `$${event.ticket_price}`}</p>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>🔞 Age:</strong> {event.age_restriction === '21_plus' ? '21+' : event.age_restriction === '18_plus' ? '18+' : 'All Ages'}
                </p>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>👥 Going:</strong> {event.going_count || 0}
                </p>
                {event.genre_tags && event.genre_tags.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    {event.genre_tags.map((genre, index) => (
                      <span
                        key={index}
                        style={{ display: 'inline-block', backgroundColor: '#e0e0e0', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', marginRight: '5px', marginTop: '5px' }}
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsPage;
